/**
 * 캐릭터 WebP 아틀라스 로더
 *
 * 기존 spriteLoader.ts는 캐릭터 스프라이트를 SVG 개별 파일 2,240장으로 관리했다.
 * 4개 에이전트가 병렬로 WebP 아틀라스(70 atlas, 140 files)를 만들었으므로, 여기서
 * 그 아틀라스를 Phaser 텍스처로 등록하고 프레임을 꺼내 쓰는 API를 제공한다.
 *
 * 아틀라스 위치:
 *   web-client/public/assets/gfx/atlas/{char}_{weapon}_walk.webp  + .json
 *   web-client/public/assets/gfx/atlas/{char}_death.webp         + .json
 *
 * JSON 형식 (TexturePacker 호환):
 *   {
 *     "meta": { "image": "...webp", "size": { "w", "h" }, ... },
 *     "frames": {
 *       "N_0":  { "frame": { "x":..., "y":..., "w":48, "h":48 }, ... },
 *       "NE_0": { ... },
 *       "E_0":  { ... },
 *       "SE_0": { ... },
 *       "S_0":  { ... }
 *       // 5방향 × 5프레임 = 25 entries
 *     }
 *   }
 *
 * **방향 최적화**: 원본 8방향 중 W/SW/NW 3개는 아틀라스에 저장하지 않는다.
 * 호출자가 좌측 방향을 넘기면 mirroredDirection()으로 우측 반전 방향(E/SE/NE)을
 * 받아 텍스처를 쓰고 sprite.setFlipX(true)로 시각적으로 좌측을 만든다. N/S는
 * 좌우 구분이 없으므로 그대로 둔다.
 */

import Phaser from 'phaser';
import {
  CharacterId,
  Direction,
  WeaponType,
  FRAME_COUNT,
  DEATH_FRAME_COUNT,
} from './spriteLoader';

/** 아틀라스 이미지가 있는 public 디렉토리. */
const ATLAS_BASE = '/assets/gfx/atlas';

/** 아틀라스에 실제로 들어 있는 5방향. W/SW/NW는 호출 측에서 flipX로 처리한다. */
export const ATLAS_DIRECTIONS: Direction[] = ['N', 'NE', 'E', 'SE', 'S'];

/** 좌측 방향 → 반전 시 참조할 우측 방향 (flipX로 좌측을 만들어낼 매핑). */
const MIRROR_MAP: Record<Direction, Direction> = {
  N: 'N',   // 북은 좌우 구분이 없다.
  S: 'S',   // 남은 좌우 구분이 없다.
  E: 'E',
  NE: 'NE',
  SE: 'SE',
  W: 'E',   // W → E 반전
  SW: 'SE', // SW → SE 반전
  NW: 'NE', // NW → NE 반전
};

/** 좌측 방향(시각적으로 반전이 필요한가). */
export function directionNeedsFlip(direction: Direction): boolean {
  return direction === 'W' || direction === 'SW' || direction === 'NW';
}

/** flipX를 적용할 때 참조할 우측 방향을 돌려준다. W → E, SW → SE, NW → NE. */
export function mirroredDirection(direction: Direction): Direction {
  return MIRROR_MAP[direction];
}

/** 걷기 atlas의 Phaser 텍스처 키 (= atlas 이름). */
export function getWalkAtlasKey(char: CharacterId, weapon: WeaponType): string {
  return `atlas|walk|${char}|${weapon}`;
}

/** 사망 atlas의 Phaser 텍스처 키. */
export function getDeathAtlasKey(char: CharacterId): string {
  return `atlas|death|${char}`;
}

/** 걷기 atlas의 이미지 URL. */
export function getWalkAtlasUrl(char: CharacterId, weapon: WeaponType): string {
  return `${ATLAS_BASE}/${char}_${weapon}_walk.webp`;
}

/** 사망 atlas의 이미지 URL. */
export function getDeathAtlasUrl(char: CharacterId): string {
  return `${ATLAS_BASE}/${char}_death.webp`;
}

/** Phaser 텍스처 시스템 안에서의 프레임명 (예: "E_0"). */
export function getWalkFrameName(direction: Direction, frame: number): string {
  const safeFrame = Math.max(0, Math.min(FRAME_COUNT - 1, Math.floor(frame)));
  return `${direction}_${safeFrame}`;
}

/** 사망 atlas의 프레임명. */
export function getDeathFrameName(direction: Direction, frame: number): string {
  const safeFrame = Math.max(0, Math.min(DEATH_FRAME_COUNT - 1, Math.floor(frame)));
  return `${direction}_${safeFrame}`;
}

// ============================================================
// atlas 로드 (Phaser의 load.atlas는 TexturePacker JSON을 그대로 받는다)
// ============================================================

/** atlas 로드 대기 상한(ms). 이벤트를 놓쳐도 이 시간 안에는 판정을 끝낸다. */
const ATLAS_LOAD_TIMEOUT_MS = 15000;

/** 이미 load가 시작된 atlas 키를 추적 — 중복 호출 시 같은 Promise를 공유한다. */
const pendingAtlas: Map<string, Promise<string>> = new Map();

/** 로드 완료된 atlas 키 (성공/실패 무관 — 실패는 호출자에서 catch). */
const loadedAtlas: Set<string> = new Set();


/**
 * atlas 파일 하나를 로드하고 완료를 기다린다.
 *
 * **loaderror는 반드시 이 키의 파일인지 확인해야 한다.** 예전 구현은
 * `once('loaderror')`로 모든 로드 실패를 잡아서, 관계없는 다른 파일이 하나라도
 * 실패하면 성공한 atlas의 Promise까지 reject됐다. 그러면 호출자가 재적용
 * 콜백을 실행하지 않아 스프라이트가 placeholder 그대로 남는다.
 *
 * 리스너는 성공·실패 어느 쪽으로 끝나든 반드시 해제한다 — 남겨두면 이후의
 * 무관한 로드 이벤트에 계속 반응한다.
 */
function loadAtlasFile(
  scene: Phaser.Scene,
  key: string,
  url: string,
  jsonUrl: string,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      clearTimeout(timer);
      scene.load.off('complete', onComplete);
      scene.load.off('loaderror', onError);
    };

    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      cleanup();
      pendingAtlas.delete(key);
      if (ok) {
        loadedAtlas.add(key);
        resolve(key);
      } else {
        reject(new Error(`Atlas load failed: ${url}`));
      }
    };

    /**
     * 완료 판정은 **텍스처가 실제로 등록됐는지**로 한다.
     *
     * `filecomplete-atlas-<key>` 이벤트에 기대면 안 된다 — 실측 결과 atlas는
     * 이 이벤트가 오지 않고 로더의 'complete'만 발생한다. 그 이벤트를 기다리던
     * 예전 구현은 로드가 성공해도 Promise가 영원히 대기 상태로 남아, 호출자의
     * 재적용 콜백이 실행되지 않고 스프라이트가 placeholder 그대로 머물렀다.
     *
     * 'complete'는 다른 로드 배치의 것일 수도 있으므로, 내 키가 아직 없으면
     * 다음 배치를 계속 기다린다.
     */
    const onComplete = () => {
      if (scene.textures.exists(key)) finish(true);
    };

    const onError = (file?: { key?: string }) => {
      // 다른 파일의 실패는 이 Promise와 무관하다.
      if (file?.key !== key) return;
      finish(false);
    };

    // 이벤트를 놓치더라도 영원히 매달리지 않도록 한 번 더 확인하고 끝낸다.
    const timer = setTimeout(() => finish(scene.textures.exists(key)), ATLAS_LOAD_TIMEOUT_MS);

    scene.load.atlas(key, url, jsonUrl);
    scene.load.on('complete', onComplete);
    scene.load.on('loaderror', onError);

    // 씬의 preload 단계가 끝난 뒤 호출되므로 로더를 직접 굴려야 한다.
    if (!scene.load.isLoading()) {
      scene.load.start();
    }
  });
}

/**
 * 캐릭터/무기 조합의 걷기 atlas를 Phaser 텍스처로 등록한다.
 * Phaser의 scene.load.atlas()는 TexturePacker 호환 JSON을 그대로 처리하므로
 * 추가 파싱 없이 frames["E_0"] 같은 키로 텍스처를 자를 수 있다.
 *
 * 이미 등록된 키면 즉시 resolve한다.
 */
export function loadCharWalkAtlas(
  scene: Phaser.Scene,
  char: CharacterId,
  weapon: WeaponType,
): Promise<string> {
  const key = getWalkAtlasKey(char, weapon);
  if (loadedAtlas.has(key) || scene.textures.exists(key)) {
    loadedAtlas.add(key);
    return Promise.resolve(key);
  }

  const existing = pendingAtlas.get(key);
  if (existing) return existing;

  const url = getWalkAtlasUrl(char, weapon);
  const jsonUrl = url.replace(/\.webp$/, '.json');

  const promise = loadAtlasFile(scene, key, url, jsonUrl);

  pendingAtlas.set(key, promise);
  return promise;
}

/**
 * 캐릭터의 사망 atlas를 Phaser 텍스처로 등록한다.
 */
export function loadCharDeathAtlas(
  scene: Phaser.Scene,
  char: CharacterId,
): Promise<string> {
  const key = getDeathAtlasKey(char);
  if (loadedAtlas.has(key) || scene.textures.exists(key)) {
    loadedAtlas.add(key);
    return Promise.resolve(key);
  }

  const existing = pendingAtlas.get(key);
  if (existing) return existing;

  const url = getDeathAtlasUrl(char);
  const jsonUrl = url.replace(/\.webp$/, '.json');

  const promise = loadAtlasFile(scene, key, url, jsonUrl);

  pendingAtlas.set(key, promise);
  return promise;
}

// ============================================================
// 프레임 적용 (스프라이트 + flipX 처리)
// ============================================================

/**
 * 스프라이트에 (char, weapon, direction, frame)에 해당하는 atlas 프레임을 적용한다.
 * 좌측 방향(directionNeedsFlip)인 경우 sprite.setFlipX(true)를 켠다.
 *
 * atlas가 아직 로드 중이면 placeholder 텍스처를 그대로 두고 로드 완료 시점에
 * 한 번 더 applyWalkSprite()를 호출해 갱신한다. 호출자는 placeholder 키로
 * add.image()를 만들어 둔 상태여야 한다.
 *
 * @returns atlas가 이미 등록돼 있으면 true (스프라이트 갱신 완료), 아니면 false
 */
export function applyWalkSprite(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image,
  char: CharacterId,
  weapon: WeaponType,
  direction: Direction,
  frame: number,
): boolean {
  const atlasKey = getWalkAtlasKey(char, weapon);
  if (!scene.textures.exists(atlasKey)) {
    // atlas가 아직 로드되지 않은 상태. 등록된 게 없으니 false를 돌려주고
    // 호출자는 loadCharWalkAtlas()를 fire-and-forget로 시작한 뒤 로드 완료 시
    // 다시 applyWalkSprite()를 호출하면 된다.
    return false;
  }

  const visualDir = mirroredDirection(direction);
  const frameName = getWalkFrameName(visualDir, frame);
  sprite.setTexture(atlasKey, frameName);
  sprite.setFlipX(directionNeedsFlip(direction));
  return true;
}

/**
 * 스프라이트에 사망 프레임을 적용한다. 사망은 8방향 모두 표현하므로 flipX가
 * 필요 없다 (애니메이션 자체가 회전을 포함하므로 우측/좌측 구분이 의미 없음).
 *
 * @returns atlas가 이미 등록돼 있으면 true, 아니면 false
 */
export function applyDeathSprite(
  scene: Phaser.Scene,
  sprite: Phaser.GameObjects.Image,
  char: CharacterId,
  direction: Direction,
  frame: number,
): boolean {
  const atlasKey = getDeathAtlasKey(char);
  if (!scene.textures.exists(atlasKey)) return false;

  const frameName = getDeathFrameName(direction, frame);
  sprite.setTexture(atlasKey, frameName);
  sprite.setFlipX(false);
  return true;
}

/** 디버깅용 캐시 상태. */
export function getAtlasCacheStats(): { loaded: number; pending: number } {
  return { loaded: loadedAtlas.size, pending: pendingAtlas.size };
}

/** 테스트용 캐시 초기화. 실제 게임 흐름에서는 호출하지 말 것. */
export function clearAtlasCache(): void {
  loadedAtlas.clear();
  pendingAtlas.clear();
}
